import { createApi } from "@reduxjs/toolkit/query/react";

import {
  ATTENDANCE_REGULARIZATION,
  ATTENDANCE_REGULARIZATION_POLICY,
  PAGINATED_ATTENDANCE_REGULARIZATION,
} from "@/constants/apiEndpoints";
import type {
  TCreateRegularizationResponse,
  IDeleteRegularizationRequest,
  TDeleteRegularizationResponse,
  TGetRegularizationRequest,
  TGetRegularizationResponse,
  TListRegularizationRequest,
  TListRegularizationResponse,
  IUpdateRegularizationRequest,
  TUpdateRegularizationResponse,
  TRegularizationStatsRequest,
  TRegularizationStatsResponse,
  TRegularizationPolicyRequest,
  TRegularizationPolicyResponse,
} from "@/types/(my-space)/attendanceRegularization.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { timeAttendanceApi } from "@/services/(my-space)/api.time-attendance";

// Regularization mutations live in this slice, but a created/edited/deleted
// request changes the attendance summary + drawer (a different RTK slice —
// `invalidatesTags` can't cross slices). Cross-invalidate the shared summary
// tag (the drawer query provides it too) so every regularization write, from
// any page, refetches the attendance views.
const invalidateAttendanceSummary = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<unknown>;
  },
) => {
  try {
    await queryFulfilled;
    dispatch(
      timeAttendanceApi.util.invalidateTags(["getTimeAttendanceSummary"]),
    );
  } catch {
    // request failed — nothing to invalidate
  }
};

export const attendanceRegularizationApi = createApi({
  reducerPath: "attendanceRegularizationApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "getPaginatedAttendanceRegularization",
    "getUserRegularizationRequest",
    "getRegularizationStats",
    "getRegularizationPolicy",
  ],
  endpoints: (builder) => ({
    // Company policy as it applies to this employee: whether they may file a
    // request at all, and the oldest date they may still file for. Read by
    // the request form to bound its date picker — the API re-checks on write.
    getRegularizationPolicy: builder.query<
      TRegularizationPolicyResponse,
      TRegularizationPolicyRequest
    >({
      query: () => ({
        url: ATTENDANCE_REGULARIZATION_POLICY,
        method: "GET",
      }),
      providesTags: ["getRegularizationPolicy"],
    }),
    getPaginatedAttendanceRegularization: builder.query<
      TListRegularizationResponse,
      TListRegularizationRequest
    >({
      query: (params) => {
        return {
          url: PAGINATED_ATTENDANCE_REGULARIZATION,
          method: "GET",
          params,
        };
      },
      providesTags: ["getPaginatedAttendanceRegularization"],
    }),
    getRegularizationStats: builder.query<
      TRegularizationStatsResponse,
      TRegularizationStatsRequest
    >({
      query: (params) => ({
        url: `${ATTENDANCE_REGULARIZATION}/stats`,
        method: "GET",
        params,
      }),
      providesTags: ["getRegularizationStats"],
    }),
    getRegularizationRequest: builder.query<
      TGetRegularizationResponse,
      TGetRegularizationRequest
    >({
      query: (body) => {
        return {
          url: `${ATTENDANCE_REGULARIZATION}/${body.id}`,
          method: "GET",
        };
      },
      providesTags: ["getUserRegularizationRequest"],
    }),
    updateRegularizationRequest: builder.mutation<
      TUpdateRegularizationResponse,
      IUpdateRegularizationRequest
    >({
      query: (body) => {
        return {
          url: `${ATTENDANCE_REGULARIZATION}/${body.id}`,
          method: "PUT",
          body: body.data,
        };
      },
      invalidatesTags: [
        "getUserRegularizationRequest",
        "getPaginatedAttendanceRegularization",
        "getRegularizationStats",
      ],
      onQueryStarted: invalidateAttendanceSummary,
    }),
    deleteAttendanceRegularization: builder.mutation<
      TDeleteRegularizationResponse,
      IDeleteRegularizationRequest
    >({
      query: (body) => {
        return {
          url: `${ATTENDANCE_REGULARIZATION}/${body.id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [
        "getUserRegularizationRequest",
        "getPaginatedAttendanceRegularization",
        "getRegularizationStats",
      ],
      onQueryStarted: invalidateAttendanceSummary,
    }),
    createRegularizationRequest: builder.mutation<
      TCreateRegularizationResponse,
      FormData
    >({
      query: (body) => {
        return {
          url: ATTENDANCE_REGULARIZATION,
          method: "POST",
          body,
        };
      },
      invalidatesTags: [
        "getPaginatedAttendanceRegularization",
        "getRegularizationStats",
      ],
      onQueryStarted: invalidateAttendanceSummary,
    }),
  }),
});

export const {
  useGetRegularizationPolicyQuery,
  useGetPaginatedAttendanceRegularizationQuery,
  useGetRegularizationStatsQuery,
  useDeleteAttendanceRegularizationMutation,
  useCreateRegularizationRequestMutation,
  useGetRegularizationRequestQuery,
  useUpdateRegularizationRequestMutation,
} = attendanceRegularizationApi;
